import {
  CalloutBanner,
  Divider,
  InputControl,
  Link,
  LinkUse,
  Toggle,
} from "@growtherapy/sprout-ui";
import { useTranslate } from "@growtherapy/i18n";
import { useLocalStorage } from "usehooks-ts";
import { deviceIsIOS } from "../../utils";
import { faClosedCaptioning } from "@fortawesome/pro-solid-svg-icons";
import { STORED_ACCESSIBILITY_SETTINGS_KEY } from "./constants";

export type AccessibilitySettings = {
  disableNotificationTimeouts: boolean;
};

export type AccessibilitySettingsTabProps = {
  onChange: (settings: AccessibilitySettings) => void;
  accessibilitySettings?: AccessibilitySettings;
  showLiveCaptioningHelp: boolean;
};

const LIVE_CAPTIONING_HELP_ARTICLE =
  "https://help.growtherapy.com/en/articles/10365766-enable-live-captioning-for-appointments";

export function AccessibilitySettingsTab({
  onChange,
  accessibilitySettings,
  showLiveCaptioningHelp,
}: AccessibilitySettingsTabProps) {
  const t = useTranslate();
  return (
    <>
      <InputControl
        label={t("telehealth-core:settings.disableNotificationTimeouts")}
        labelClassName="ml-2"
        layoutClassName="flex flex-row-reverse items-center justify-end rebrand:flex-row-reverse rebrand:items-center rebrand:justify-end"
        className="mb-0"
      >
        <Toggle
          checked={accessibilitySettings?.disableNotificationTimeouts || false}
          onChange={(e) => {
            onChange({
              disableNotificationTimeouts: e.target.checked,
            });
          }}
        />
      </InputControl>
      <div className="mt-4 text-sm text-neutral-700 rebrand:typography-label-small rebrand:text-text-default">
        {t(
          "telehealth-core:settings.keepAllNotificationsOnTheScreenUntilYouDismissThem",
        )}
      </div>

      {showLiveCaptioningHelp && (
        <>
          <Divider className="mt-4 mb-6 border-neutral-600" />

          <CalloutBanner
            use="yellow"
            type="alert"
            icon={faClosedCaptioning}
            heading={t("telehealth-core:settings.liveCaptionsAvailable")}
            headingAs="h3"
            className="bg-neutral-200"
          >
            {t(
              "telehealth-core:settings.toViewCaptionsEnableLiveCaptionsInYourBrowser",
            )}{" "}
            <Link
              href={LIVE_CAPTIONING_HELP_ARTICLE}
              use={LinkUse.Neutral}
              size="sm"
              external
            >
              {t("telehealth-core:settings.learnHowToEnableLiveCaptions")}
            </Link>
            <span className="sr-only">
              {t(
                "telehealth-core:settings.inYourBrowserOrDevicesAccessibilitySettings",
              )}
            </span>
          </CalloutBanner>
        </>
      )}
    </>
  );
}

export function AccessibilitySettingsTabWrapper() {
  const [accessibilitySettings, setAccessibilitySettings] = useLocalStorage(
    STORED_ACCESSIBILITY_SETTINGS_KEY,
    {
      disableNotificationTimeouts: false,
    },
  );

  return (
    <AccessibilitySettingsTab
      onChange={setAccessibilitySettings}
      accessibilitySettings={accessibilitySettings}
      showLiveCaptioningHelp={!deviceIsIOS()}
    />
  );
}
